import React from "react";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useContactUs } from "./useContactUs";
import { useContactUsStyles } from "./ContactUs.styles";
import { ReactComponent as AvatarIcon } from "../../assets/contactUsAvatar.svg";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TEXTAREA_BASE_HEIGHT = 20;

export const ContactUsModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dir = t("css.side") as "ltr" | "rtl";
  const classes = useContactUsStyles();
  const { sendMessage, loading } = useContactUs();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
  });

  const onSubmit = async (data: any) => {
    const res = await sendMessage(data);
    if (res.success) {
      reset();
      onClose();
      onSuccess();
    }
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock={true}>
      <div className={classes.modalWrapper} dir={dir}>
        <div className={classes.upperSection}>
          <div className={classes.headerRow}>
            <div className={classes.title}>{t("contact.title")}</div>
            <div className={classes.closeBtn} onClick={onClose}>
              ✕
            </div>
          </div>

          <div className={classes.flexRow}>
            <AvatarIcon />
            <div className={classes.callUsContainer}>
              <div className={classes.callUs}>{t("contact.callUs")}</div>
              <a href="tel:+9721234567" className={classes.phoneNumber}>
                +972 123 45 67
              </a>
            </div>
          </div>

          <div className={classes.orSendMessage}>{t("contact.orSend")}</div>
        </div>

        {/* Form */}
        <form
          className={classes.contactUsForm}
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Email */}
          <div className={classes.formControl}>
            <input
              className={`${classes.input} ${
                errors.email ? classes.inputErrorWithPlaceholder : ""
              }`}
              placeholder={t("contact.email")}
              {...register("email", {
                required: t("contact.errors.emailRequired") as string,
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t("contact.errors.emailInvalid"),
                },
              })}
            />
            <div className={classes.inputError}>
              {(errors?.email?.message as string) || ""}
            </div>
          </div>

          {/* Mobile */}
          <div className={classes.formControl}>
            <input
              className={`${classes.input} ${
                errors.mobile ? classes.inputErrorWithPlaceholder : ""
              }`}
              placeholder={t("contact.mobile")}
              {...register("mobile", {
                required: t("contact.errors.mobileRequired") as string,
                pattern: {
                  value: /^(05\d{8}|0[2-9]\d{7})$/,
                  message: t("contact.errors.mobileInvalid"),
                },
              })}
            />
            <div className={classes.inputError}>
              {(errors?.mobile?.message as string) || ""}
            </div>
          </div>

          {/* Message */}
          <div>
            <textarea
              className={`${classes.textarea} ${
                errors.message ? classes.textareaError : ""
              }`}
              placeholder={t("contact.message")}
              {...register("message", {
                required: t("contact.errors.messageRequired") as string,
              })}
              onInput={(e) => {
                const el = e.currentTarget;

                // Reset first (required to shrink)
                el.style.height = `${TEXTAREA_BASE_HEIGHT}px`;

                // Grow only if there's content
                if (el.value.trim().length > 0) {
                  const style = window.getComputedStyle(el);
                  const padding =
                    parseInt(style.paddingTop) + parseInt(style.paddingBottom);

                  el.style.height = `${el.scrollHeight - padding}px`;
                }
              }}
            />

            <div className={classes.inputError}>
              {(errors?.message?.message as string) || ""}
            </div>
          </div>

          <button
            className={classes.submitBtn}
            type="submit"
            disabled={!isValid || loading}
          >
            {loading ? t("contact.sending") : t("contact.send")}
          </button>
        </form>
      </div>
    </Modal>
  );
};
